// Flora Dine digital visiting card.
// A single SVG is the source of truth: it is rendered as the on-page preview
// AND rasterized to PNG / embedded into a PDF, so every download matches the
// preview exactly. The QR encodes a vCard so a scan saves Flora Dine as a
// contact. No network calls  - fonts and the logo are inlined for export.

import { CONTACT } from './contact';

export const CARD_W = 640;
export const CARD_H = 1012;

const CREAM = '#f8f4ed';
const GREEN = '#2f5848';
const GREEN_DARK = '#243a2d';
const TERRACOTTA = '#9a5f3d';
const GOLD = '#e8b923';
const FOREGROUND = '#243a2d';
const MUTED = '#6b5d49';
const BORDER = '#e4dac9';

// ── vCard (encoded inside the QR and offered as a .vcf download) ─────────────
export function buildVCard(): string {
  return [
    'BEGIN:VCARD',
    'VERSION:3.0',
    'FN:Flora Dine',
    'ORG:Flora Dine',
    'TITLE:Premium Biodegradable Tableware',
    `TEL;TYPE=WORK,VOICE:${CONTACT.phoneIndiaTel}`,
    `TEL;TYPE=WORK,VOICE:${CONTACT.phoneUKTel}`,
    `EMAIL;TYPE=WORK:${CONTACT.email}`,
    'URL:https://floradine.com',
    'ADR;TYPE=WORK:;;Varanasi;Uttar Pradesh;221002;India',
    'NOTE:Purely Natural, Truly Disposable',
    'END:VCARD',
  ].join('\r\n');
}

// Escape text destined for SVG/XML text nodes.
function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Place a 24×24-authored icon at a top-left point with uniform scale.
function icon(
  inner: string,
  x: number,
  y: number,
  scale: number,
  stroke: string,
  fill = 'none',
  sw = 1.8
): string {
  return `<g transform="translate(${x} ${y}) scale(${scale})" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round">${inner}</g>`;
}

const ICONS = {
  phone:
    '<path d="M6.5 4h2.6l1.3 3.2-1.6 1.2a10.5 10.5 0 0 0 4.8 4.8l1.2-1.6 3.2 1.3v2.6a1.8 1.8 0 0 1-2 1.8A15.5 15.5 0 0 1 4.7 6a1.8 1.8 0 0 1 1.8-2z"/>',
  mail: '<path d="M3.5 6.5h17v11h-17z"/><path d="M3.8 7l8.2 5.6 8.2-5.6"/>',
  globe:
    '<circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3c2.8 2.8 2.8 15.2 0 18"/><path d="M12 3c-2.8 2.8-2.8 15.2 0 18"/>',
  pin: '<path d="M12 21c4.5-4.6 6.5-8 6.5-11A6.5 6.5 0 0 0 5.5 10c0 3 2 6.4 6.5 11z"/><circle cx="12" cy="10" r="2.4"/>',
  leaf: '<path d="M4 20c0-8 6-14 16-14 0 10-6 15-14 15-1.2 0-2-.4-2-1z"/><path d="M8 18c3-3.5 6.5-5.5 10-6.5"/>',
  // "Why Flora Dine" bullet icons  - one per proof point (certified, verified,
  // single contact, export), authored to the same 24×24 stroke grid as above.
  certificate:
    '<path d="M13 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9z"/><path d="M13 3v6h6"/><path d="M8.5 14l2 2 4-4.5"/>',
  shield:
    '<path d="M12 3l7 2.5v5.5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V5.5z"/><path d="M9 11.5l2 2 4-4"/>',
  person: '<circle cx="12" cy="8" r="3.2"/><path d="M5.5 19.5a6.5 6.5 0 0 1 13 0"/>',
  truck:
    '<path d="M3 6.5h10.5v8.5H3z"/><path d="M13.5 9.5h3.5l3 3v2.5h-6.5z"/><circle cx="7" cy="17.5" r="1.7"/><circle cx="17.5" cy="17.5" r="1.7"/>',
};

interface ContactRow {
  iconKey: keyof typeof ICONS;
  label: string;
  value: string;
  sub: string;
}

const ROWS: ContactRow[] = [
  {
    iconKey: 'phone',
    label: 'CALL',
    value: `${CONTACT.phoneIndia}  ·  ${CONTACT.phoneUK}`,
    sub: 'India  ·  United Kingdom',
  },
  {
    iconKey: 'mail',
    label: 'EMAIL',
    value: CONTACT.email,
    sub: 'Wholesale & export enquiries',
  },
  {
    iconKey: 'globe',
    label: 'WEBSITE',
    value: 'floradine.com',
    sub: 'Explore the range and request samples',
  },
  {
    iconKey: 'pin',
    label: 'VISIT',
    value: 'Varanasi, Uttar Pradesh',
    sub: 'Proudly made in India',
  },
];

interface Bullet {
  iconKey: keyof typeof ICONS;
  text: string;
}

const BULLETS: Bullet[] = [
  { iconKey: 'certificate', text: 'Certified, plastic-free' },
  { iconKey: 'shield', text: 'Verified, compliance-led sourcing' },
  { iconKey: 'person', text: 'One contact, order to delivery' },
  { iconKey: 'truck', text: 'Wholesale & export from India' },
];

export interface BuildCardOpts {
  logoDataUrl: string | null;
  qrMatrix: boolean[][] | null;
  // 'preview' uses live page font variables; 'export' uses inlined @font-face.
  mode: 'preview' | 'export';
  fontFaceCss?: string;
}

export function buildCardSvg({
  logoDataUrl,
  qrMatrix,
  mode,
  fontFaceCss = '',
}: BuildCardOpts): string {
  const headlineFF =
    mode === 'export' ? "'Playfair Display', Georgia, serif" : 'var(--font-headline)';
  const bodyFF = mode === 'export' ? "'Inter', system-ui, sans-serif" : 'var(--font-body)';

  // ── Header ─────────────────────────────────────────────────────────────
  const header = `
    <path d="M0 32 Q0 0 32 0 H608 Q640 0 640 32 V300 H0 Z" fill="${GREEN}"/>
    <rect x="0" y="300" width="640" height="5" fill="${TERRACOTTA}"/>
    <circle cx="320" cy="92" r="50" fill="${CREAM}"/>
    <circle cx="320" cy="92" r="50" fill="none" stroke="${GOLD}" stroke-width="2" opacity="0.6"/>
    ${
      logoDataUrl
        ? `<image href="${logoDataUrl}" x="280" y="52" width="80" height="80" clip-path="url(#fd-logo-clip)" preserveAspectRatio="xMidYMid meet"/>`
        : ''
    }
    <text x="320" y="192" text-anchor="middle" font-family="${headlineFF}" font-size="52" font-weight="700" letter-spacing="3" fill="${CREAM}">FLORA DINE</text>
    <text x="320" y="226" text-anchor="middle" font-family="${bodyFF}" font-size="18" font-weight="400" fill="${CREAM}" opacity="0.85">Purely Natural, Truly Disposable</text>
    <text x="320" y="264" text-anchor="middle" font-family="${bodyFF}" font-size="12.5" font-weight="600" letter-spacing="3" fill="${GOLD}">PREMIUM BIODEGRADABLE TABLEWARE</text>
  `;

  // ── Contact rows ───────────────────────────────────────────────────────
  const rowCenters = [388, 472, 556, 640];
  const rows = ROWS.map((row, i) => {
    const cy = rowCenters[i];
    return `
      <circle cx="70" cy="${cy}" r="23" fill="${GREEN}"/>
      ${icon(ICONS[row.iconKey], 70 - 11, cy - 11, 22 / 24, CREAM)}
      <text x="116" y="${cy - 8}" font-family="${bodyFF}" font-size="12" font-weight="600" letter-spacing="2" fill="${MUTED}">${esc(row.label)}</text>
      <text x="116" y="${cy + 16}" font-family="${bodyFF}" font-size="21" font-weight="600" fill="${FOREGROUND}">${esc(row.value)}</text>
      <text x="116" y="${cy + 36}" font-family="${bodyFF}" font-size="13" font-weight="400" fill="${MUTED}">${esc(row.sub)}</text>
    `;
  }).join('');

  const divider = `<line x1="70" y1="712" x2="570" y2="712" stroke="${BORDER}" stroke-width="1.5"/>`;

  // ── QR (encodes the vCard) ─────────────────────────────────────────────
  const qrPlateX = 64;
  const qrPlateY = 738;
  const qrPlate = 170;
  const qrInner = 146;
  const qrX = qrPlateX + (qrPlate - qrInner) / 2;
  const qrY = qrPlateY + (qrPlate - qrInner) / 2;
  let qrPath = '';
  if (qrMatrix && qrMatrix.length) {
    const n = qrMatrix.length;
    const m = qrInner / n;
    const parts: string[] = [];
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        if (qrMatrix[r][c]) {
          const px = +(qrX + c * m).toFixed(2);
          const py = +(qrY + r * m).toFixed(2);
          const s = +m.toFixed(3);
          parts.push(`M${px} ${py}h${s}v${s}h-${s}z`);
        }
      }
    }
    qrPath = `<path d="${parts.join('')}" fill="${GREEN_DARK}"/>`;
  }
  const qr = `
    <rect x="${qrPlateX}" y="${qrPlateY}" width="${qrPlate}" height="${qrPlate}" rx="14" fill="#ffffff" stroke="${BORDER}" stroke-width="1.5"/>
    ${qrPath}
    <text x="${qrPlateX + qrPlate / 2}" y="928" text-anchor="middle" font-family="${bodyFF}" font-size="12.5" font-weight="700" letter-spacing="2.5" fill="${TERRACOTTA}">SCAN TO SAVE</text>
  `;

  // ── "Why Flora Dine" bullets ───────────────────────────────────
  const bulletX = 262;
  const bullets = `
    <text x="${bulletX}" y="762" font-family="${bodyFF}" font-size="12.5" font-weight="700" letter-spacing="2.5" fill="${TERRACOTTA}">WHY FLORA DINE</text>
    ${BULLETS.map((b, i) => {
      const ly = 800 + i * 32;
      // Each proof point carries its own outline icon (audit request): certified,
      // verified, single contact, export.
      return `${icon(ICONS[b.iconKey], bulletX, ly - 15, 19 / 24, GREEN, 'none', 1.8)}<text x="${bulletX + 30}" y="${ly}" font-family="${bodyFF}" font-size="15.5" font-weight="500" fill="${FOREGROUND}">${esc(b.text)}</text>`;
    }).join('')}
  `;

  // ── Footer ─────────────────────────────────────────────────────────────
  const footer = `
    <path d="M0 940 H640 V980 Q640 1012 608 1012 H32 Q0 1012 0 980 Z" fill="${GREEN}"/>
    ${icon(ICONS.leaf, 40, 964, 16 / 24, 'none', GOLD, 0)}
    <text x="66" y="982" font-family="${bodyFF}" font-size="15" font-weight="500" fill="${CREAM}">Compostable Tableware Co.</text>
    <text x="600" y="982" text-anchor="end" font-family="${bodyFF}" font-size="17" font-weight="700" fill="${CREAM}">floradine.com</text>
  `;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${CARD_W}" height="${CARD_H}" viewBox="0 0 ${CARD_W} ${CARD_H}" role="img" aria-label="Flora Dine digital visiting card">
    <defs>
      <clipPath id="fd-logo-clip"><circle cx="320" cy="92" r="46"/></clipPath>
      ${fontFaceCss ? `<style>${fontFaceCss}</style>` : ''}
    </defs>
    <rect x="1" y="1" width="${CARD_W - 2}" height="${CARD_H - 2}" rx="31" fill="${CREAM}" stroke="${BORDER}" stroke-width="2"/>
    ${header}
    ${rows}
    ${divider}
    ${qr}
    ${bullets}
    ${footer}
  </svg>`;
}

// ── Asset / font inlining for self-contained exports ─────────────────────────
function bufferToBase64(buf: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export async function fetchAsDataUrl(url: string): Promise<string> {
  const res = await fetch(url);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Collect the page's brand @font-face rules and re-emit them with the font
// bytes inlined as data URLs, re-mapped to plain family names so the isolated
// export SVG can use them. Best-effort: returns '' on any failure.
export async function collectBrandFontCss(): Promise<string> {
  const wanted = [
    { match: 'playfair', family: 'Playfair Display' },
    { match: 'inter', family: 'Inter' },
  ];
  const out: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList | null;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // cross-origin sheet
    }
    if (!rules) continue;
    for (const rule of Array.from(rules)) {
      if (!(rule instanceof CSSFontFaceRule)) continue;
      const famRaw = (rule.style.getPropertyValue('font-family') || '').toLowerCase();
      const target = wanted.find((w) => famRaw.includes(w.match));
      if (!target) continue;
      const m = rule.cssText.match(/url\(["']?([^"')]+)["']?\)/);
      if (!m) continue;
      let url = m[1];
      // next/font authors font URLs relative to the stylesheet (e.g.
      // "../media/x.woff2"); resolve against the sheet, not the page URL.
      if (!url.startsWith('data:')) {
        url = new URL(url, sheet.href || document.baseURI).href;
      }
      try {
        const res = await fetch(url);
        const b64 = bufferToBase64(await res.arrayBuffer());
        const weight = rule.style.getPropertyValue('font-weight') || '400';
        const style = rule.style.getPropertyValue('font-style') || 'normal';
        const fmt = url.includes('.woff2') ? 'woff2' : url.includes('.woff') ? 'woff' : 'opentype';
        out.push(
          `@font-face{font-family:'${target.family}';font-style:${style};font-weight:${weight};font-display:block;src:url(data:font/${fmt};base64,${b64}) format('${fmt}');}`
        );
      } catch {
        // skip this rule
      }
    }
  }
  return out.join('');
}

// ── Rasterize / download ─────────────────────────────────────────────────────
// Rasterize the card SVG to a canvas. Pass `bg` to flatten transparency
// (needed for JPEG, which has no alpha channel).
function rasterize(svg: string, scale: number, bg?: string): Promise<HTMLCanvasElement> {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = async () => {
      try {
        if (img.decode) {
          try {
            await img.decode();
          } catch {
            /* decode is best-effort */
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = CARD_W * scale;
        canvas.height = CARD_H * scale;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas 2D context unavailable');
        ctx.scale(scale, scale);
        if (bg) {
          ctx.fillStyle = bg;
          ctx.fillRect(0, 0, CARD_W, CARD_H);
        }
        ctx.drawImage(img, 0, 0, CARD_W, CARD_H);
        URL.revokeObjectURL(url);
        resolve(canvas);
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to render card SVG'));
    };
    img.src = url;
  });
}

function triggerDownload(href: string, filename: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function downloadCardPng(exportSvg: string): Promise<void> {
  const canvas = await rasterize(exportSvg, 3);
  const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Could not create PNG');
  const url = URL.createObjectURL(blob);
  triggerDownload(url, 'FloraDine-Visiting-Card.png');
  URL.revokeObjectURL(url);
}

export async function downloadCardPdf(exportSvg: string): Promise<void> {
  // Flatten onto white and embed as JPEG (DCTDecode)  - keeps the PDF small;
  // a transparent PNG would be stored uncompressed by jsPDF (tens of MB).
  const canvas = await rasterize(exportSvg, 3, '#ffffff');
  const jpeg = canvas.toDataURL('image/jpeg', 0.95);
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: [CARD_W, CARD_H], orientation: 'portrait' });
  doc.addImage(jpeg, 'JPEG', 0, 0, CARD_W, CARD_H);
  doc.save('FloraDine-Visiting-Card.pdf');
}

export function downloadVCard(): void {
  const blob = new Blob([buildVCard()], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, 'FloraDine.vcf');
  URL.revokeObjectURL(url);
}
